/**
 * Spec №12 Stage C + №13.1 (§2.9) — WIP & billing worksheet **v2**.
 *
 * Left: unbilled WIP grouped by matter (derived, never stored — G2). Right: for a
 * selected matter, the arrangement picker (§2.7) scopes the lines; each line can be
 * selected to **write off** or **include**; then Prepare → approval-gated Post
 * (freezes the lines, renders the PDF). Settlement stays in the Trust place.
 *
 * Write-off note: today the safe operation is a status flip to `written_off`
 * (drops the entry from WIP). The typed-reason `write_off_time {entry_ids[],
 * reason}` verb is PROPOSED (design-pack §3) and gated on David's 28.3 write-off
 * taxonomy — so the reason picker below is a placeholder and no new business logic
 * is invented in the UI (the L-4/G1 rule).
 */
import { useMemo, useState } from "react";
import { Link } from "react-router";
import { useMatters, useTimeEntries, usePrepareInvoice, usePostInvoice, useWriteOffTime, type DraftInvoice } from "../../hooks/use-time-billing";
import { ArrangementPicker } from "./ArrangementPicker";
import { money } from "../../components/ledger/format";
import { showToast } from "../../components/primitives";
import { formatUnits, useTimeUnitMode } from "../../lib/format-units";

const amtOf = (t: Record<string, unknown>): number => Math.round(((t.units as number) ?? 0) * ((t.rate_snapshot_minor as number) ?? 0));
const isWip = (t: Record<string, unknown>): boolean => t.status === "wip" && (t.billable ?? "yes") !== "no";
// Placeholder reasons — the real set arrives with David's 28.3 taxonomy + the verb.
const WRITE_OFF_REASONS = ["Duplicate entry", "Non-billable / goodwill", "Over-servicing", "Rate adjustment", "Other"];

export function WipBillingWorksheetPage() {
  const { mode } = useTimeUnitMode();
  const matters = useMatters();
  const entries = useTimeEntries();
  const prepare = usePrepareInvoice();
  const post = usePostInvoice();
  const writeOff = useWriteOffTime();

  const [selected, setSelected] = useState<string>("");
  const [arrangementId, setArrangementId] = useState("");
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [reason, setReason] = useState("");
  const [draft, setDraft] = useState<DraftInvoice | null>(null);
  const [postedNo, setPostedNo] = useState<string | null>(null);

  const byMatter = useMemo(() => {
    const map = new Map<string, { total: number; lines: Record<string, unknown>[] }>();
    for (const t of entries.data ?? []) {
      if (!isWip(t)) continue;
      const mid = t.matter as string;
      const g = map.get(mid) ?? { total: 0, lines: [] };
      g.total += amtOf(t); g.lines.push(t);
      map.set(mid, g);
    }
    return map;
  }, [entries.data]);

  const matterName = (id: string): string => (matters.data ?? []).find((m) => m.id === id)?.title as string ?? id;
  const grand = [...byMatter.values()].reduce((s, g) => s + g.total, 0);
  const selGroup = selected ? byMatter.get(selected) : undefined;
  const scopedLines = useMemo(() => {
    const lines = selGroup?.lines ?? [];
    return arrangementId && arrangementId !== "__all__" ? lines.filter((t) => t.fee_arrangement === arrangementId) : lines;
  }, [selGroup, arrangementId]);
  const pickedLines = scopedLines.filter((t) => picked.has(t.id as string));
  const pickedTotal = pickedLines.reduce((s, t) => s + amtOf(t), 0);

  function select(mid: string) { setSelected(mid); setArrangementId(""); setPicked(new Set()); setReason(""); setDraft(null); setPostedNo(null); }
  const toggle = (id: string) => setPicked((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  async function doWriteOff() {
    if (pickedLines.length === 0) return;
    try {
      for (const t of pickedLines) await writeOff.mutateAsync({ entry_id: t.id as string });
      showToast(`Wrote off ${pickedLines.length} entr${pickedLines.length === 1 ? "y" : "ies"} · ${money(pickedTotal)}`, "success");
      setPicked(new Set()); setReason(""); void entries.refetch();
    } catch (e) { showToast((e as Error).message, "error"); }
  }
  async function doPrepare() {
    if (!selected) return;
    try { setDraft(await prepare.mutateAsync({ matter_id: selected, ...(arrangementId && arrangementId !== "__all__" ? { fee_arrangement_id: arrangementId } : {}) })); setPostedNo(null); }
    catch (e) { showToast((e as Error).message, "error"); }
  }
  async function doPost() {
    if (!draft) return;
    try {
      const r = await post.mutateAsync({ draft_invoice_id: draft.draft_invoice_id, matter_id: selected });
      setPostedNo(r.invoice_no); setDraft(null); void entries.refetch();
      showToast(`Invoice ${r.invoice_no} posted`, "success");
    } catch (e) { showToast((e as Error).message, "error"); }
  }

  return (
    <div className="screen vt">
      <div className="tb-wsheet">
        {/* LEFT — unbilled WIP by matter */}
        <div className="tb-card">
          <div className="tb-card__h">Unbilled WIP · by matter<span className="spacer" /><span className="count money">{money(grand)}</span></div>
          <div className="tb-card__b flush">
            {entries.isLoading ? (
              <div style={{ padding: 14 }}><div className="skeleton sk-line" style={{ width: "60%" }} /><div className="skeleton sk-line" style={{ width: "80%" }} /></div>
            ) : byMatter.size === 0 ? (
              <div className="empty-state" style={{ padding: 28 }}><h3>No unbilled WIP</h3><p>Every matter’s recorded time is billed or written off. Record time in the day view.</p><Link to="/app/time/day"><button data-variant="primary">Open day view</button></Link></div>
            ) : (
              [...byMatter.entries()].map(([mid, g]) => (
                <div className="tb-mgroup" key={mid}>
                  <div className={`tb-mgroup__h${selected === mid ? " sel" : ""}`} onClick={() => select(mid)}>
                    <strong>{matterName(mid)}</strong><span className="muted tiny">{mid}</span><span className="sum money">{money(g.total)}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* RIGHT — line selection · write-off · prepare · post */}
        <div className="tb-card">
          <div className="tb-card__h">{selected ? `Worksheet · ${selected}` : "Worksheet"}<span className="spacer" />{draft ? <span className="pill"><span className="dot" />draft</span> : null}</div>
          <div className="tb-assembly">
            {!selected ? (
              <div className="muted tiny" style={{ padding: "10px 0" }}>Select a matter on the left to bill or write off its WIP.</div>
            ) : postedNo ? (
              <>
                <div className="guard guard--ok"><svg className="mark" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6}><path d="M20 6 9 17l-5-5" /></svg><div>Invoice <strong>{postedNo}</strong> posted and frozen. Settle from trust to office in the Trust place — corrections are credit notes, never edits.</div></div>
                <div className="row" style={{ gap: 8 }}><span className="spacer" /><Link to="/app/billing/invoices"><button>View invoices →</button></Link><button onClick={() => select(selected)}>Bill another</button></div>
              </>
            ) : (
              <>
                <div className="dotline" style={{ marginBottom: 10 }}>
                  <ArrangementPicker matterId={selected} value={arrangementId} onChange={setArrangementId} includeAll />
                  <span className="spacer" style={{ flex: 1 }} />
                  <span className="muted tiny">{picked.size} selected · {money(pickedTotal)}</span>
                </div>

                <table className="ledger-table" style={{ marginBottom: 10 }}><thead><tr><th style={{ width: 28 }} /><th>Date</th><th>Narrative</th><th>Arrangement</th><th className="num">Units</th><th className="num">WIP</th></tr></thead><tbody>
                  {scopedLines.map((t) => (
                    <tr key={t.id as string}>
                      <td><input type="checkbox" checked={picked.has(t.id as string)} onChange={() => toggle(t.id as string)} /></td>
                      <td className="row-date">{(t.entry_date as string) ?? "—"}</td>
                      <td>{(t.narrative as string) ?? "Time"}</td>
                      <td className="muted">{(t.fee_arrangement as string) ?? "—"}</td>
                      <td className="num">{formatUnits((t.units as number) ?? 0, mode)}</td>
                      <td className="num money">{money(amtOf(t))}</td>
                    </tr>
                  ))}
                  {scopedLines.length === 0 ? <tr><td colSpan={6} className="muted tiny">No unbilled WIP in this scope.</td></tr> : null}
                </tbody></table>

                {/* write-off (proposed verb — placeholder reason) */}
                <div className="card" style={{ marginBottom: 10 }}>
                  <div className="card__h">Write off <span className="sub">status flip today · typed write_off_time awaits the 28.3 taxonomy</span></div>
                  <div className="card__b">
                    <div className="formfield"><label>Typed reason (28.3 taxonomy — awaiting David's export)</label>
                      <select value={reason} onChange={(e) => setReason(e.target.value)}><option value="">— select —</option>{WRITE_OFF_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}</select>
                    </div>
                    <div className="dotline">
                      <button data-variant="primary" disabled={picked.size === 0 || writeOff.isPending} onClick={() => void doWriteOff()}>{writeOff.isPending ? "Writing off…" : `Write off ${picked.size} · ${money(pickedTotal)}`}</button>
                      <span className="muted tiny">drops them from WIP (today a status flip; the typed reason is captured once write_off_time lands).</span>
                    </div>
                  </div>
                </div>

                {/* draft assembly + post */}
                <div className="tb-atot"><span>Unbilled WIP {arrangementId && arrangementId !== "__all__" ? "(scoped)" : ""}</span><span className="money">{money((selGroup?.total ?? 0))}</span></div>
                {draft ? (
                  <>
                    <div className="tb-approve"><strong>Posting requires approval.</strong> Post allocates the next invoice number, freezes {draft.line_count} line{draft.line_count === 1 ? "" : "s"} ({money(draft.total_minor)}) immutably, and marks the source time invoiced. Corrections after posting are credit notes, never edits.</div>
                    <div className="row" style={{ gap: 8 }}><button onClick={() => setDraft(null)}>Discard draft</button><span className="spacer" /><button data-variant="primary" onClick={() => void doPost()} disabled={post.isPending}>{post.isPending ? "Posting…" : "Post invoice → render PDF"}</button></div>
                  </>
                ) : (
                  <div className="row" style={{ gap: 8 }}><span className="spacer" /><button data-variant="primary" onClick={() => void doPrepare()} disabled={prepare.isPending || !selGroup}>{prepare.isPending ? "Preparing…" : "Prepare invoice"}</button></div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
